/* eslint-disable react/prop-types */
import React from 'react';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';

/**
 * User 360 — "Free access" tab (079).
 *
 * Every free access grant this member has ever been given, newest first. It reads the same
 * endpoint as the Free Grants page with one member pinned, so there is no second definition of
 * what a grant is or how it is worded: `length_label`, `ends_label`, `was_label` and
 * `billing_label` are all written by the server and printed here verbatim.
 *
 * ── TWO THINGS THIS TAB DELIBERATELY DOES ────────────────────────────────────
 *
 * 1. `status=all`. The endpoint defaults to active-only, which is right for a page asking "who
 *    has free access right now" and wrong for a history. A revoked grant is often the single
 *    most useful row here — it is the one someone will be asked about.
 *
 * 2. No date range. A grant history is a lifecycle, not a window, so the shared DateRangeBar
 *    does not apply (the tab is left out of DATE_DRIVEN for exactly this reason). Reading half
 *    of an audit trail is worse than reading none of it.
 *
 * Read-only. Granting starts from the identity card above; revoking stays on the Free Grants
 * page, where the grant's own record and its store history are in front of you.
 *
 * ── NO TALLY STRIP ───────────────────────────────────────────────────────────
 *
 * A "<n> grants · <n> active · <n> expired · <n> revoked" line sat above the list and was
 * removed on 2026-09-08, by request. The list IS the count — it is one member's grants, not a
 * page of an estate-wide table, so it is short enough to read whole, and each row already
 * carries its own Active / Expired / Revoked pill. `counts` and `pagination` are still in the
 * response and still drive the Free Grants page's own summary; this tab just reads neither.
 */

const STATUS_LABEL = { active: 'Active', expired: 'Expired', revoked: 'Revoked' };

// Reuses the Free Grants page's own pill classes so a status means the same thing, and looks
// the same, on both screens.
const statusClass = (status) => `fg-pill fg-pill--${STATUS_LABEL[status] ? status : 'none'}`;

// A billing outcome only earns visual weight when it needs acting on. Success and
// "not applicable" are the normal cases and stay quiet.
const BILLING_TONE = {
  failed: 'fg-pill--failed',
  user_action_required: 'fg-pill--waiting',
  partial: 'fg-pill--waiting',
  pending: 'fg-pill--waiting',
};

const Fact = ({ label, value }) => (
  value ? (
    <div className="ud-grant__fact">
      <span className="k">{label}</span>
      <span className="v">{value}</span>
    </div>
  ) : null
);

const FreeAccessTab = ({ userId, authGet }) => {
  const { status, data, reload } = useTabData(async () => {
    const params = new URLSearchParams({ user_id: String(userId), status: 'all', limit: '100' });
    const res = await authGet(`/admin/api/free-access-grants?${params.toString()}`);
    return res.data.data || {};
  }, [userId]);

  if (status === 'loading') return <TabLoading />;
  if (status === 'error') return <TabError onRetry={reload} />;

  const grants = data?.grants || [];

  if (!grants.length) {
    return (
      <TabEmpty
        title="No free access granted"
        message="This member has never been given free premium access. Use Grant Free Access on the card to the left."
        icon="🎁"
      />
    );
  }

  return (
    <div data-testid="ud-free-access">
      <div className="au-panel">
        <div className="au-panel__head">
          <h4 className="au-panel__title">Free access history</h4>
        </div>

        <div className="ud-grant-list">
          {grants.map((g) => (
            <div className="ud-grant" key={g.id} data-testid={`ud-grant-${g.id}`}>
              <div className="ud-grant__head">
                <span className="ud-grant__len">
                  {g.length_label || (Number(g.is_lifetime) === 1 ? 'Lifetime' : '—')}
                </span>
                <span className={statusClass(g.status)} data-testid="ud-grant-status">
                  {STATUS_LABEL[g.status] || g.status || '—'}
                </span>
                <span className="ud-grant__on">{g.created_at || '—'}</span>
              </div>

              <div className="ud-grant__facts">
                <Fact label="Before" value={g.was_label} />
                {/* endDateLabel already prints an em dash for a lifetime grant — the 100-year
                    sentinel is never sent and must never be reconstructed here. */}
                <Fact label="Access until" value={g.ends_label} />
                <Fact label="Reason" value={g.reason} />
                <Fact label="Granted by" value={g.admin_email} />
                {g.status === 'revoked' && (
                  <>
                    <Fact label="Revoked on" value={g.revoked_at} />
                    <Fact label="Revoke reason" value={g.revoke_reason} />
                  </>
                )}
              </div>

              {g.billing_label && (
                <div className="ud-grant__billing">
                  <span
                    className={`fg-pill ${BILLING_TONE[g.billing_status] || 'fg-pill--none'}`}
                    data-testid="ud-grant-billing"
                  >
                    {g.billing_label}
                  </span>
                  {/* The sentence matters more than the label when a store call did not go
                      through: the member HAS their access and only the billing is outstanding. */}
                  {g.billing_message && (
                    <span className="ud-grant__billing-msg">{g.billing_message}</span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FreeAccessTab;
